import camelcaseKeys from 'camelcase-keys'
import { OFFLINE_NODE_VISIBILITY_HOURS } from '../constants'
import { isEmpty } from '../utilities/general'
import { RealtimePostgresChangesPayload } from '@supabase/realtime-js'
import { Database } from '../types/supabase'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import useOrganizations from './useOrganizations'
import useNodeMetadata from './useNodeMetadata'
import useServices from './useServices'
import useUpgrades from './useUpgrades'
import useSetPlaces from './useSetPlaces'
import { needsMetadataRefresh } from '../utilities/nodeMetadataRefresh'
import { nodeEntersStore } from '../utilities/nodeEntersStore'
import { eventClock, rowsAfterRead } from '../utilities/rowsAfterRead'
import { readWhenStreamStarts } from '../utilities/streamStart'
import { shouldApplyNodeEvent } from '../utilities/nodeEventScope'
import { membershipMoved, withEventNetworks, withNetworkIds, withReadNetworks } from '../utilities/nodeNetworks'
import { isNodeOffline } from '../utilities/nodeStatus'
import { callTool } from '../lib/tools'

// `owner_user_id` gates Remove Node (app#371): the node's owner or an org Owner/Admin may remove it.
// `owner_org_id` is the node's TENANT (G-1, app#385): every node control asks the caller's role in
// that org, never in the org of the node's network (`useOrgRole(node.ownerOrgId)`).
const NODE_FIELDS = `id, created_at, deleted_at, name, offline_at, online_at, public_key, restart, router, token_id, channel_id, labels, owner_user_id, owner_org_id, node_networks(network_id, name, created_at)`

export { isNodeOffline }

export interface NodesState {
  nodes: NodeRecord[]
  hideOffline: boolean
  // ⚠ Whether `nodes` holds EVERY node of the active org, or only the ones the `hideOffline`
  // filter keeps. It describes the LOADED data, not the next query: `hideOffline` flips the
  // instant the button is clicked, while the new node set arrives a round trip later, so a
  // reader that derives completeness from `hideOffline` states a total the data cannot support
  // for the width of that window (and forever, if the refetch fails — `get` swallows the error).
  nodesComplete: boolean
  /**
   * The org of the last read of `nodes`, or `undefined` before the first read: the same field as
   * `useNetwork.loadedOrgId`. A node's page tests it against the active org before it says that a
   * node is not there (app#473): an empty or old list does not say that.
   */
  loadedOrgId: string | null | undefined
  set: (state: Partial<NodesState>) => void
  get: () => Promise<void>
  /**
   * The org of node `id`: a direct read of the one row, with no "hide offline" filter and no org
   * filter. `false` when the reader can read no such node, null when the read failed (app#478).
   * ⚠ RLS bounds the read to the reader's own orgs. It does not put the node in the store.
   */
  orgOf: (id: string) => Promise<string | false | null>
  setChannel: (id: string, channelId: string | null) => Promise<void>
  delete: (id: string, force?: boolean) => Promise<void>
  add: (node: NodeRecord) => void
  rename: (id: string, name: string) => Promise<void>
  update: (node: Partial<NodeRecord>) => void
  remove: (nodeId?: string) => void
  subscribe: () => () => void
  reset: () => void
}

const state: Pick<NodesState, 'nodes' | 'hideOffline' | 'nodesComplete' | 'loadedOrgId'> = {
  nodes: [],
  hideOffline: true,
  nodesComplete: false,
  loadedOrgId: undefined,
}

// ⚠ Each get() takes a number, and only the LATEST one may write the store. Several reads can
// overlap — an org switch, the `hideOffline` button, a realtime (re)join and a registration all
// start one — and the replies do not arrive in order. Without this, a read started for the old
// org that lands last puts that org's nodes back in the store under the new active org.
let latestGet = 0

// ⚠⚠ A READ MUST NOT OVERWRITE A NEWER EVENT (app#439), the same rule as in `useServices`
// (app#437). This store has two writers: `get()` and the realtime handler. The answer of a read
// is as old as the moment the database made it, and an event can arrive between that moment and
// the answer. The handler marks each event it applies on this clock, by node id. A read asks the
// clock when it is SENT, and its answer does not touch a node that an event changed after that
// (`rowsAfterRead`). Without it, a node that went offline during a read showed online again, and
// a node that connected during a read was removed again, until the next event.
const events = eventClock()

/**
 * Read the rows of the node-derived stores for the nodes that have just entered this store
 * (app#436), with ONE query for each store.
 *
 * ⚠⚠ CALL IT ON EACH PATH THAT PUTS A NODE IN THE STORE WITHOUT A FULL READ OF THE NODE-DERIVED
 * SET AFTER IT. `useServices`, `useUpgrades` and `useSetPlaces` hold rows only for the nodes of
 * their last `get()`, and nothing else brings the rows of a later node: its page showed no
 * services until a reload. `controller.loadState`, `useOrganizations.setActiveOrg` and `Filter` read the full set,
 * so they do not call it. The realtime handler and the rejoin below do, and `readNodesAgain` does.
 *
 * ⚠ `node_metadata` is not in this set: it has its own rule (`needsMetadataRefresh`), which also
 * reads again for a node the store holds.
 */
export function loadNodeDerived(nodeIds: string[]) {
  if (!nodeIds.length) return
  void useServices.getState().getForNodes(nodeIds)
  void useUpgrades.getState().getForNodes(nodeIds)
  // The place a person set for the node (nobgp-router#1976): without it a node placed by hand is
  // drawn at the place of its address (app#489).
  void useSetPlaces.getState().getForNodes(nodeIds)
}

/**
 * Read the nodes again, then read the node-derived rows of each node that is NEW after the read.
 *
 * ⚠⚠ A CALLER OF `useNode.get()` THAT DOES NOT READ THE FULL NODE-DERIVED SET AFTER IT CALLS THIS
 * INSTEAD (`Register`, `useTasks.deprovision`). A node read can bring a node the store did not
 * hold (its realtime event was lost), and that node then has no services until a reload (app#436).
 * `src/utilities/nodeEntersStore.test.ts` fails on a new bare caller.
 *
 * ⚠ The rows are read AFTER the node read lands, however long it takes: the reads keep rows only
 * for a node the store holds.
 */
export async function readNodesAgain(): Promise<void> {
  const before = new Set(useNode.getState().nodes.map(n => n.id))
  const orgId = useOrganizations.getState().activeOrgId
  await useNode.getState().get()
  // An org switch in between reads the full set itself, and every node would read as new.
  if (useOrganizations.getState().activeOrgId !== orgId) return
  loadNodeDerived(useNode.getState().nodes.flatMap(n => (n.id && !before.has(n.id) ? [n.id] : [])))
}

const useNode = create<NodesState>()((set, get) => ({
  ...state,
  set,
  get: async () => {
    const seq = ++latestGet
    const newer = events.readStarts()
    const { hideOffline } = get()

    // Scope to the active org (1.3). services/nodeMetadata/upgrades derive from the loaded nodes,
    // so this cascades to them. UX scoping only; RLS already bounds reads to the caller's orgs.
    //
    // ⚠⚠ THE SCOPE IS THE NODE'S TENANT, `nodes.owner_org_id`, NEVER THE ORG OF ITS NETWORK
    // (G-1, app#385 and app#388, docs/PERMISSIONS.md § 2 and § 4 "see"). The members of the
    // node's org see it. The org of a network it is attached to sees it only later, with
    // cross-org attach (§ 6), so a node of ANOTHER org in one of the active org's networks is not
    // loaded here. It shows under its own org. A node of the active org in another org's network,
    // or in no network, IS loaded, and the Nodes tab draws it in its own group
    // (`utilities/nodeGroups.ts`).
    const activeOrgId = useOrganizations.getState().activeOrgId
    const cutoff = (() => {
      const cutoffDate = new Date()
      cutoffDate.setHours(cutoffDate.getHours() - OFFLINE_NODE_VISIBILITY_HOURS)
      return cutoffDate.toISOString()
    })()
    const query = supabase.from('nodes').select(NODE_FIELDS).is('deleted_at', null)
    if (activeOrgId) query.eq('owner_org_id', activeOrgId)
    if (hideOffline) query.or(`offline_at.is.null,offline_at.gte.${cutoff}`)
    const { data, error } = await query
    if (seq !== latestGet) return

    console.log('get nodes', data)

    if (error) console.warn(error)
    else {
      const read = (data ?? []).map(row => withNetworkIds(camelcaseKeys(row))) as NodeRecord[]
      // The answer, except where an event is newer than it (see `events`).
      // ⚠ A node the event kept must still be a node of the active org. A held node of the
      // PREVIOUS org gets its events until this answer replaces it (`shouldApplyNodeEvent`,
      // app#366), and such an event must not keep it in the store under the new org.
      const nodes = withReadNetworks(rowsAfterRead(get().nodes, read, () => true, newer), read).filter(
        n => !activeOrgId || n.ownerOrgId === activeOrgId
      )
      set({ nodes, nodesComplete: !hideOffline, loadedOrgId: activeOrgId })
    }
  },
  orgOf: async id => {
    const { data, error } = await supabase.from('nodes').select('owner_org_id').eq('id', id).is('deleted_at', null).limit(1)
    // ⚠ An id that is not a uuid (a path typed or cut by hand) is no object: the database refuses
    // the comparison (22P02). That is a clear "no", not a failed read.
    if (error?.code === '22P02') return false
    if (error) {
      console.warn(error)
      return null
    }
    return (data ?? [])[0]?.owner_org_id || false
  },
  // Set the node's release channel through the router's `node_channel_set` (app#429, router#1756).
  // THROWS, so the page can say why under the field. The node's owner or an org Owner/Admin may;
  // anyone else gets `forbidden`.
  //
  // ⚠ It was the last node write on PostgREST (app#373, docs/PERMISSIONS.md § 9). Router migration
  // 0229's trigger stays as the database backstop.
  // ⚠ An empty `channel_id` clears it: the node then follows its network's channel. The tool
  // refuses a call that does not send the field.
  setChannel: async (id, channelId) => {
    await callTool('node_channel_set', { node_id: id, channel_id: channelId ?? '' })
  },
  // Rename a node through the router's `node_rename` (app#373). THROWS rather than alerting, so the
  // caller turns the refusal into readable text with renameErrorMessage: the expected failures are a
  // name already taken in this network (`already_exists`) and a reader who does not control the
  // node (`forbidden`: only its owner or an org Owner/Admin may rename it).
  //
  // ⚠ It was a PostgREST write, and router migration 0140 made it Admin-only there, so the node's
  // own Member owner could never rename it. The router asks the owner-or-Admin rule.
  // ⚠ The router lower-cases the name before it stores it; realtime brings the stored name back.
  rename: async (id, name) => {
    await callTool('node_rename', { node_id: id, new_name: name })
  },
  // Remove a node through the router's `node_delete` (app#373). THROWS, so the page can show the
  // router's reason. The node's owner or an org Owner/Admin may; the router asks at each call.
  //
  // ⚠⚠ IT DOES WHAT THE POSTGREST SOFT DELETE DID NOT. A PROVISIONED node is deprovisioned first,
  // so its compute stops. An ONLINE node that was registered by hand is refused with
  // `failed_precondition` unless `force` is sent, and with `force` the router also closes its
  // connection. The page asks for that in the confirmation, and sends `force` only then.
  delete: async (id, force) => {
    await callTool('node_delete', force ? { node_id: id, force: true } : { node_id: id })
  },
  add: node => {
    const nodes = [...get().nodes]
    nodes.push(node)
    set({ nodes })
  },
  update: node => {
    const { id, ...data } = node
    const nodes = [...get().nodes]

    const index = nodes.findIndex(n => n.id === id)
    if (index === -1) {
      // A tombstone for a node we are not holding is nothing to ADD. get() excludes
      // deleted rows, so pushing one here would put a deleted node on screen — the exact
      // inverse of the bug the splice below fixes, and reachable today: hideOffline is on
      // by default, so an offline node was never loaded, and deleting it elsewhere (another
      // tab, or an MCP deprovision_node) delivers an in-org UPDATE that lands right here.
      if (data.deletedAt) return
      nodes.push(node as NodeRecord)
    } else if (isEmpty(data) || data.deletedAt) {
      nodes.splice(index, 1)
    } else {
      nodes[index] = { ...nodes[index], ...node }
    }

    set({ nodes })
  },
  remove: nodeId => {
    let nodes = [...get().nodes]
    nodes = nodes.filter(n => n.id !== nodeId)
    set({ nodes })
  },
  subscribe: () => {
    const { update, remove } = get()
    // ⚠⚠ RE-READ AFTER EVERY (RE)JOIN (app#366). Realtime delivery is not a guarantee: the
    // channel drops and rejoins — a sleeping laptop, a lost socket, or the overlay coming up
    // on the very machine the browser runs on — and every event in that gap is gone for good,
    // because the store is only ever re-read by a page load or an org switch. The re-read costs
    // one query per join and makes a lost event self-healing.
    // ⚠⚠ THE READ IS MADE WHEN THE CHANGE STREAM STARTS, NOT ON `SUBSCRIBED` (app#439,
    // `readWhenStreamStarts`): the server starts the stream after it answers the join, and a
    // read that is answered before the start loses each change in between.
    // ⚠ The re-read passes no event through `needsMetadataRefresh`, and `node_metadata` is not
    // live (app#132), so a node that came online in the gap is refreshed here, by the same rule.
    // ⚠ A node that is NEW after the re-read has no services and no upgrade record in their
    // stores: they were read for the node ids before the gap (app#436).
    const stream = readWhenStreamStarts(() => {
      const before = new Map(get().nodes.map(n => [n.id, n]))
      const orgId = useOrganizations.getState().activeOrgId
      void get()
        .get()
        .then(() => {
          // An org switch in between re-reads `node_metadata` itself, and every node would read as new.
          if (useOrganizations.getState().activeOrgId !== orgId) return
          // ⚠ ONE call for all the new nodes: every node can be new here (the first node read
          // failed, or the offline filter changed), and one read for each node was a storm.
          const entered: string[] = []
          for (const n of get().nodes) {
            if (needsMetadataRefresh('UPDATE', before.get(n.id), n)) void useNodeMetadata.getState().getOne(n.id)
            if (n.id && !before.has(n.id)) entered.push(n.id)
          }
          loadNodeDerived(entered)
        })
    })
    const asked = new Map<string | undefined, string | null | undefined>()
    const nodeSubscription = supabase
      .channel('nodes-all')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'nodes' },
        async (payload: RealtimePostgresChangesPayload<Database['public']['Tables']['nodes']['Row']>) => {
          console.log('Node change received!', payload.eventType, payload)
          const node = camelcaseKeys(payload.new) as NodeRecord
          // A DELETE has no new row: its id is in the old one.
          const eventId = node.id ?? (payload.old as { id?: string } | undefined)?.id
          // Stay scoped to the active org, the way `get()` is: a node whose TENANT is the active
          // org (G-1, app#385 and app#388). Anything else is another org you belong to and
          // would mix orgs. ⚠ A node the store ALREADY HOLDS is
          // applied regardless (app#366): it cleared this filter when it was loaded, and a
          // dropped event is permanent, because nothing re-reads `nodes` short of a reload.
          if (
            !shouldApplyNodeEvent({
              eventType: payload.eventType,
              activeOrgId: useOrganizations.getState().activeOrgId,
              heldNodeIds: get().nodes.flatMap(n => (n.id ? [n.id] : [])),
              nodeId: eventId,
              ownerOrgId: node.ownerOrgId,
            })
          )
            return
          // Mark this event, for a node read that is in flight (see `events`).
          if (eventId) events.mark(eventId)
          // Read BEFORE add/update: the comparison is against the node as the store held it.
          const held = get().nodes.find(n => n.id === node.id)
          const column = (node as { networkId?: string | null }).networkId
          if (needsMetadataRefresh(payload.eventType, held, node)) void useNodeMetadata.getState().getOne(node.id)
          switch (payload.eventType) {
            // ⚠ An INSERT goes through `update`, which adds a node the store does not hold and
            // merges one it holds. A node read (a rejoin, `readNodesAgain`) can put the row in the
            // store BEFORE its INSERT event arrives, and `add` then held the node twice.
            case 'INSERT':
            case 'UPDATE':
              update(withEventNetworks(node, !!held) as NodeRecord)
              break
            case 'DELETE':
              remove(payload.old.id)
              break
          }
          // AFTER add/update: the reads keep their rows only for a node the store holds.
          const entered = !!node.id && nodeEntersStore(payload.eventType, held, node)
          if (entered) loadNodeDerived([node.id])
          // ⚠ `node_networks` has no realtime event, so this is the one signal of another session's
          // attach or detach. A node that enters has only the network on its row, so its full
          // memberships come with the read. `asked` holds the column of the last read for a node:
          // a column the read does not change, for a reader who cannot see that membership, must
          // not read the nodes at each heartbeat.
          if (entered || (held && membershipMoved(held.networkIds, column) && asked.get(node.id) !== column)) {
            asked.set(node.id, column)
            void readNodesAgain()
          }
        }
      )
      .on('system', {}, stream.onSystem)
      .subscribe(stream.onStatus)

    return () => {
      console.log('Unsubscribing from node changes')
      stream.stop()
      supabase.removeChannel(nodeSubscription)
    }
  },
  reset: () => set({ ...state }),
}))

export default useNode
